/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  Download,
  Loader2,
  ArrowLeft,
  FileText,
  Clock,
  File,
  CheckCircle,
  SquarePen,
  MessageSquare,
  XCircle,
  Send,
  Trash2,
  Filter,
  Briefcase,
} from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import { useToast } from 'src/hooks/use-toast';
import { BillingService } from 'src/services/api/BillingService';
import { useProformaInvoiceInfo } from 'src/services/api/queries/billingQueries';
import { useQueryClient } from '@tanstack/react-query';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ProformaInvoicePDF from 'src/components/ProformaInvoicePDF/ProformaInvoicePDF';
import { Alert } from 'src/components/common/Alert';
import { BILLING_STATUS_VALUES } from 'src/utils/statusConstants';
import { StatusPill } from 'src/components/ui/status-pill';

const transformApiResponse = (apiData) => {
  const { header, invoice_details: invoiceDetails, items, meta } = apiData;

  return {
    id: meta.id,
    invoiceNumber: header.invoice_number,
    invoiceDate: header.invoice_date,
    dueDate: header.due_date,
    batchReference: header.batch_reference,
    client: {
      name: header.customer.name || 'N/A',
      id: header.customer.id,
      gstin: header.customer.gstin || 'N/A',
      address: {
        line1: header.customer.address_line_1 || '',
        street: header.customer.street || '',
        zipCode: header.customer.zip_code || '',
      },
    },
    ahedge: {
      name: header.ahedge.name || 'N/A',
      address: header.ahedge.address,
      cityStatePin: header.ahedge.city_state_pin,
      gstin: header.ahedge.gstin,
    },
    invoiceDetails: {
      description: meta.description,
      subTotal: invoiceDetails.sub_total,
      taxAmount: invoiceDetails.tax_amount,
      taxPercentage: invoiceDetails.tax_percentage,
      totalAmount: invoiceDetails.total_amount,
      currency: invoiceDetails.currency,
    },
    items: items.map((item) => ({
      id: item.id,
      candidateName: item.candidate_name,
      candidateId: item.candidate_id,
      requirementRegId: item.registration_id,
      requirementTitle: item.requirement_title,
      requirementId: item.requirement_id,
      poNumber: item.po_number,
      poId: item.po_id,
      description: item.description,
      joiningDate: item.joining_date,
      milestoneType: item.milestone_type,
      installmentOrderNumber: item.installment_order_number,
      milestoneText: item.milestone_text,
      milestoneDate: item.milestone_date,
      ctcAmount: item.ctc_amount,
      costPerCandidate: item.cost_per_candidate,
      amount: item.amount,
    })),
    meta: {
      id: meta.id,
      status: meta.status,
      customerCategory: meta.customer_category,
      milestoneType: meta.milestone_type,
      isProforma: meta.is_proforma,
      createdAt: meta.created_at,
      updatedAt: meta.updated_at,
    },
  };
};

const InfoCard = ({ title, children, className = '', rounded = false }) => (
  <div
    className={`bg-card p-4 ${className} ${rounded ? 'rounded-xl border border-border' : ''}`}
  >
    {title && (
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2">
        {title}
      </h3>
    )}
    {children}
  </div>
);

const StatusTag = ({ status }) => {
  const label = BILLING_STATUS_VALUES[status] || status;
  let textColor = 'slate-600';
  let borderColor = 'slate-200';
  let bgColor = 'slate-50';
  let icon = null;

  switch (status) {
    case 'GENERATED':
      textColor = 'amber-700';
      borderColor = 'amber-200';
      bgColor = 'amber-50';
      icon = <Clock className="w-3.5 h-3.5" />;
      break;
    case 'DRAFT':
      textColor = 'blue-700';
      borderColor = 'blue-200';
      bgColor = 'blue-50';
      icon = <File className="w-3.5 h-3.5" />;
      break;
    case 'APPROVED':
      textColor = 'emerald-600';
      borderColor = 'emerald-200';
      bgColor = 'emerald-50';
      icon = <CheckCircle className="w-3.5 h-3.5" />;
      break;
    case 'MODIFIED':
      textColor = 'violet-700';
      borderColor = 'violet-200';
      bgColor = 'violet-50';
      icon = <SquarePen className="w-3.5 h-3.5" />;
      break;
  }

  return (
    <StatusPill
      name={label}
      icon={icon}
      textColor={textColor}
      borderColor={borderColor}
      bgColor={bgColor}
    />
  );
};

const DISPUTE_TYPE_LABELS = {
  CTC_UPDATE: 'CTC Update',
  CANDIDATE_REMOVAL: 'Candidate Removal',
};

const ProformaInvoiceInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    data: proformaResponse,
    isLoading,
    isError,
    error,
  } = useProformaInvoiceInfo(id);
  // Derived from the cached query; the verify / dispute-submit flows invalidate
  // ['proforma-invoices', 'info', id] so this refetches instead of local patching.
  const proformaInvoiceData = proformaResponse?.success
    ? transformApiResponse(proformaResponse.data)
    : null;
  const [showVerifyAlert, setShowVerifyAlert] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [isLoadingDisputes, setIsLoadingDisputes] = useState(false);
  const [disputeUpdates, setDisputeUpdates] = useState({});
  const [showSubmitDisputeAlert, setShowSubmitDisputeAlert] = useState(false);
  const [isSubmittingDisputes, setIsSubmittingDisputes] = useState(false);
  const [disputeFilter, setDisputeFilter] = useState(['pending', 'closed']); // multiselect: array of 'pending' and/or 'closed'
  const [activeTab, setActiveTab] = useState('details');
  const [selectedDisputeId, setSelectedDisputeId] = useState(null);
  const [showDisputeChat, setShowDisputeChat] = useState(false);

  // Fetch disputes when status is MODIFIED
  useEffect(() => {
    const loadDisputes = async () => {
      // if (
      //   proformaInvoiceData?.meta?.status !== 'MODIFIED' ||
      //   proformaInvoiceData?.meta?.status !== 'GENERATED'
      // ) {
      //   return;
      // }

      try {
        setIsLoadingDisputes(true);
        const response = await BillingService.getInvoiceDisputes(id);
        if (response?.success) {
          setDisputes(response.data?.items || []);
        }
      } catch (err) {
        console.error('Failed to load disputes:', err);
        toast({
          title: 'Warning',
          description: 'Failed to load dispute information',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingDisputes(false);
      }
    };

    loadDisputes();
  }, [id, proformaInvoiceData?.meta?.status, toast]);

  // Initialize dispute updates when disputes are loaded (only for pending disputes)
  useEffect(() => {
    if (disputes.length > 0) {
      const initialUpdates = {};
      // Only initialize for pending (not verified) disputes
      disputes
        .filter((d) => !d.is_verified)
        .forEach((dispute) => {
          initialUpdates[dispute.dispute_item_id] = {
            agreed_amount: dispute.agreed_amount || '',
            reason: '',
            is_agreed: null, // null = not selected, true = agree, false = disagree
            is_rejected: null, // For CANDIDATE_REMOVAL: null = not selected, false = agree, true = disagree
          };
        });
      setDisputeUpdates(initialUpdates);
    }
  }, [disputes]);

  const handleVerifyProformaInvoice = async () => {
    setIsVerifying(true);
    try {
      const response = await BillingService.verifyProformaInvoice(id);
      if (response?.success) {
        toast({
          title: 'Success',
          description: 'Proforma invoice has been verified successfully',
          variant: 'success',
        });

        // Refresh the cached invoice so the status reflects the change
        queryClient.invalidateQueries({
          queryKey: ['proforma-invoices', 'info', id],
        });

        setShowVerifyAlert(false);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to verify proforma invoice',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4">Loading proforma invoice details...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-500 p-4 border border-red-300 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">
            Error Loading Proforma Invoice
          </h2>
          <p>
            {error?.message || 'Failed to load proforma invoice information'}
          </p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!proformaInvoiceData) {
    return <div>No proforma invoice data found.</div>;
  }

  const currencySymbol =
    proformaInvoiceData.invoiceDetails.currency === 'INR' ? '₹' : '$';

  const getOrdinalSuffix = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '';

    const tens = number % 100;
    if (tens >= 11 && tens <= 13) return 'th';

    switch (number % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const getMilestoneLabel = (item) => {
    if (item.milestoneType === 'REPLACEMENT') {
      return 'Replacement';
    }

    if (item.milestoneType === 'NORMAL') {
      const order = Number(item.installmentOrderNumber);
      if (!Number.isNaN(order) && order > 0) {
        const suffix = getOrdinalSuffix(order);
        return `${order}${suffix} installment`;
      }
      return 'Installment';
    }

    return item.installmentOrderNumber || item.milestoneText || '-';
  };

  // Helper to get candidate info from invoice items by invoice_item_id
  const getItemForDispute = (invoiceItemId) => {
    return proformaInvoiceData.items.find((item) => item.id === invoiceItemId);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `${currencySymbol}${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Filter disputes into pending (not verified) and verified (history)
  const pendingDisputes = disputes.filter((d) => !d.is_verified);
  const verifiedDisputes = disputes.filter((d) => d.is_verified);

  // Handle agreed amount change
  const handleAgreedAmountChange = (disputeItemId, value) => {
    setDisputeUpdates((prev) => ({
      ...prev,
      [disputeItemId]: {
        ...prev[disputeItemId],
        agreed_amount: value,
      },
    }));
  };

  // Handle comment/reason change
  const handleReasonChange = (disputeItemId, value) => {
    setDisputeUpdates((prev) => ({
      ...prev,
      [disputeItemId]: {
        ...prev[disputeItemId],
        reason: value,
      },
    }));
  };

  // Handle agree/disagree for CTC_UPDATE
  const handleFeeUpdateDecision = (disputeItemId, isAgreed, disputedAmount) => {
    setDisputeUpdates((prev) => ({
      ...prev,
      [disputeItemId]: {
        ...prev[disputeItemId],
        is_agreed: isAgreed,
        // If agreed, set agreed_amount to disputed_amount; if disagree, clear it for manual input
        agreed_amount: isAgreed ? disputedAmount : '',
      },
    }));
  };

  // Handle agree/disagree for CANDIDATE_REMOVAL
  const handleCandidateRemovalDecision = (disputeItemId, isAgreed) => {
    setDisputeUpdates((prev) => ({
      ...prev,
      [disputeItemId]: {
        ...prev[disputeItemId],
        is_rejected: !isAgreed, // is_rejected = false for agree, true for disagree
      },
    }));
  };

  // Handle opening dispute chat panel
  const handleOpenDisputeChat = (disputeItemId) => {
    setSelectedDisputeId(disputeItemId);
    setShowDisputeChat(true);
  };

  // Check if at least one pending dispute has a complete response
  const isDisputeUpdateComplete = (dispute) => {
    const update = disputeUpdates[dispute.dispute_item_id];
    if (!update) return false;

    // Comment is required for all disputes
    if (!update.reason || update.reason.trim() === '') return false;

    // For CTC_UPDATE: must have made a decision (agree/disagree)
    if (dispute.dispute_type === 'CTC_UPDATE') {
      if (update.is_agreed === null) return false;
      // If disagree, agreed_amount must be filled
      if (update.is_agreed === false) {
        if (!update.agreed_amount || update.agreed_amount === '') return false;
      }
    }

    // For CANDIDATE_REMOVAL: must have made a decision (agree/disagree)
    if (dispute.dispute_type === 'CANDIDATE_REMOVAL') {
      if (update.is_rejected === null) return false;
    }

    return true;
  };

  const isDisputeFormValid = () => {
    const pending = disputes.filter((d) => !d.is_verified);
    if (pending.length === 0) return false;

    // Allow submission when at least one pending dispute has a captured response
    return pending.some((dispute) => isDisputeUpdateComplete(dispute));
  };

  // Handle dispute submission (only submit pending disputes)
  const handleSubmitDisputes = async () => {
    setIsSubmittingDisputes(true);
    try {
      const pending = disputes.filter((d) => !d.is_verified);
      // Only submit disputes with complete responses so partial submissions are allowed
      const readyToSubmit = pending.filter((dispute) =>
        isDisputeUpdateComplete(dispute)
      );

      if (readyToSubmit.length === 0) {
        toast({
          title: 'No responses to submit',
          description:
            'Please complete at least one dispute response to submit.',
          variant: 'destructive',
        });
        return;
      }

      const details = readyToSubmit.map((dispute) => {
        const update = disputeUpdates[dispute.dispute_item_id];

        const payload = {
          invoice_item_id: dispute.invoice_item_id,
          candidate_id: dispute.candidate_id,
          dispute_type: dispute.dispute_type,
          reason: update.reason,
          dispute_item_id: dispute.dispute_item_id,
        };

        // For CTC_UPDATE: set agreed_amount based on decision
        if (dispute.dispute_type === 'CTC_UPDATE') {
          payload.agreed_amount = update.is_agreed
            ? parseFloat(dispute.disputed_ctc_amount) // If agreed, use disputed CTC amount
            : parseFloat(update.agreed_amount); // If disagree, use manually entered amount
        } else if (dispute.dispute_type === 'CANDIDATE_REMOVAL') {
          // For CANDIDATE_REMOVAL: set is_rejected based on decision
          payload.is_rejected = update.is_rejected; // false = agree, true = disagree
          payload.agreed_amount = 0; // For candidate removal, always send 0
        } else {
          payload.agreed_amount = 0;
        }

        return payload;
      });

      const response = await BillingService.updateInvoiceDispute(id, {
        details,
      });

      if (response?.success) {
        toast({
          title: 'Success',
          description: 'Dispute response has been submitted successfully',
          variant: 'success',
        });
        setShowSubmitDisputeAlert(false);

        // Refresh the cached invoice details
        queryClient.invalidateQueries({
          queryKey: ['proforma-invoices', 'info', id],
        });

        // Refetch disputes
        try {
          setIsLoadingDisputes(true);
          const disputesResponse = await BillingService.getInvoiceDisputes(id);
          if (disputesResponse?.success) {
            setDisputes(disputesResponse.data?.items || []);
            // Reset dispute updates after refetch
            setDisputeUpdates({});
          }
        } catch (err) {
          console.error('Failed to refetch disputes:', err);
        } finally {
          setIsLoadingDisputes(false);
        }

        // Close side panel if opene
        setShowDisputeChat(false);
        setSelectedDisputeId(null);
      } else {
        throw new Error(
          response?.message || 'Failed to submit dispute response'
        );
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to submit dispute response',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingDisputes(false);
    }
  };

  return (
    <div className="text-left space-y-4 bg-muted">
      <PageHeader
        title={`Proforma Invoice # ${proformaInvoiceData.invoiceNumber}`}
        actions={
          <>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate('/proforma-invoices')}
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              Back to Proforma Invoices
            </Button>
            <PDFDownloadLink
              document={<ProformaInvoicePDF data={proformaInvoiceData} />}
              fileName={`ProformaInvoice_${proformaInvoiceData.invoiceNumber}.pdf`}
              className="no-underline"
            >
              {({ loading }) => (
                <Button
                  disabled={loading}
                  variant="outline"
                  size="headerSm"
                  className="gap-1.5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      Download PDF
                      <Download className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </PDFDownloadLink>
          </>
        }
      />

      <div className="p-4 px-40 md:px-20 flex gap-4 flex-col">
        <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-md text-primary flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Proforma Invoice
            </h2>
            {proformaInvoiceData.meta.status === 'DRAFT' && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-emerald-500 text-white hover:bg-emerald-600"
                onClick={() => setShowVerifyAlert(true)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Verify
              </Button>
            )}
          </div>
          {/* Tabs for MODIFIED status */}
          {disputes.length > 0 ? (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="details" className="text-xs">
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="disputes" className="text-xs">
                    <MessageSquare className="w-3.5 h-3.5 mr-1" />
                    Disputes
                    {pendingDisputes.length > 0 && (
                      <span className="ml-1 bg-brand-bg-subtle text-primary px-2 py-0.5 rounded-full text-[10px]">
                        {pendingDisputes.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                {activeTab === 'disputes' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                      >
                        <Filter className="h-3.5 w-3.5 mr-1" />
                        {disputeFilter.length === 2
                          ? 'All'
                          : disputeFilter.includes('pending')
                            ? `Pending (${pendingDisputes.length})`
                            : `Closed (${verifiedDisputes.length})`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="end">
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-muted p-2 rounded">
                          <Checkbox
                            checked={disputeFilter.includes('pending')}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setDisputeFilter([...disputeFilter, 'pending']);
                              } else {
                                setDisputeFilter(
                                  disputeFilter.filter((f) => f !== 'pending')
                                );
                              }
                            }}
                          />
                          <span className="text-xs">
                            Pending ({pendingDisputes.length})
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer hover:bg-muted p-2 rounded">
                          <Checkbox
                            checked={disputeFilter.includes('closed')}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setDisputeFilter([...disputeFilter, 'closed']);
                              } else {
                                setDisputeFilter(
                                  disputeFilter.filter((f) => f !== 'closed')
                                );
                              }
                            }}
                          />
                          <span className="text-xs">
                            Closed ({verifiedDisputes.length})
                          </span>
                        </label>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              <TabsContent value="details" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-border pb-6">
                  <InfoCard title="From" className="pl-0">
                    <h4 className="text-sm font-semibold text-base">
                      {proformaInvoiceData.ahedge.name}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      {proformaInvoiceData.ahedge.address}
                    </p>
                    <p className="text-xs text-gray-600">
                      {proformaInvoiceData.ahedge.cityStatePin}
                    </p>
                    <p className="text-xs mt-2 flex gap-2 items-center">
                      <span className="text-muted-foreground font-normal text-[11px]">
                        GSTIN:
                      </span>{' '}
                      <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                        {proformaInvoiceData.ahedge.gstin}
                      </span>
                    </p>
                  </InfoCard>

                  <InfoCard title="To">
                    <h4 className="text-sm font-semibold text-base">
                      {proformaInvoiceData.client.name}
                    </h4>
                    {proformaInvoiceData.client.address && (
                      <>
                        {proformaInvoiceData.client.address.line1 && (
                          <p className="text-xs text-gray-600 mt-1">
                            {proformaInvoiceData.client.address.line1}
                          </p>
                        )}
                        {(proformaInvoiceData.client.address.street ||
                          proformaInvoiceData.client.address.zipCode) && (
                          <p className="text-xs text-gray-600">
                            {[
                              proformaInvoiceData.client.address.street,
                              proformaInvoiceData.client.address.zipCode,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        )}
                      </>
                    )}
                    {proformaInvoiceData.client.gstin &&
                      proformaInvoiceData.client.gstin !== 'N/A' && (
                        <p className="text-xs mt-2 flex gap-2 items-center">
                          <span className="text-muted-foreground font-normal text-[11px]">
                            GSTIN:
                          </span>{' '}
                          <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                            {proformaInvoiceData.client.gstin}
                          </span>
                        </p>
                      )}
                  </InfoCard>
                  <InfoCard rounded className="!bg-card grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1 justify-between">
                      <span className="text-xs text-gray-700">
                        Invoice Date
                      </span>
                      <span className="text-xs font-bold">
                        {proformaInvoiceData.invoiceDate}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 justify-between">
                      <span className="text-xs text-gray-700">Due Date</span>
                      <span className="text-xs font-bold">
                        {proformaInvoiceData.dueDate}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 justify-between">
                      <span className="text-xs text-gray-700">Status</span>
                      <span className="text-xs font-bold w-fit">
                        <StatusTag status={proformaInvoiceData.meta.status} />
                      </span>
                    </div>
                  </InfoCard>
                </div>

                <InfoCard title="Summary" className="mb-6 px-0">
                  <div className="rounded-xl border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="border-b border-border">
                          <tr className="bg-card text-muted-foreground">
                            <th className="p-3 py-2 text-left font-normal">
                              DESCRIPTION
                            </th>
                            <th className="p-3 py-2 text-right font-normal">
                              NOS
                            </th>
                            <th className="p-3 py-2 text-right font-normal">
                              TOTAL
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            key={`item-summary`}
                            className="border-b border-slate-100"
                          >
                            <td className="p-3 font-medium">
                              {proformaInvoiceData.invoiceDetails.description}
                            </td>
                            <td className="p-3 text-right">
                              {proformaInvoiceData.items.length}
                            </td>
                            <td className="p-3 text-right">
                              {currencySymbol}
                              {proformaInvoiceData.invoiceDetails.subTotal?.toLocaleString(
                                'en-IN',
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end p-4 pt-3">
                    <div className="w-full max-w-xs text-xs space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">
                          GST (
                          {proformaInvoiceData.invoiceDetails.taxPercentage}%)
                        </span>
                        <span className="text-slate-700 font-medium">
                          {currencySymbol}
                          {proformaInvoiceData.invoiceDetails.taxAmount?.toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>
                      </div>
                      <div className="border-t border-border my-2" />
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground-strong">
                          Grand Total
                        </span>
                        <span
                          className="text-[15px] font-bold text-primary"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {currencySymbol}
                          {proformaInvoiceData.invoiceDetails.totalAmount?.toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </InfoCard>
                <div className="mt-8 space-y-6">
                  <div className="bg-muted border border-gray-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-slate-500">
                      This is a system-generated proforma invoice. No signature
                      required.
                    </p>
                  </div>

                  <div className="flex flex-col gap-4 justify-between items-start pt-4">
                    <div className="flex-1">
                      <h4 className="font-bold text-xs mb-2">Terms</h4>
                      <ul className="text-xs text-slate-500 space-y-1">
                        <li>
                          • Payment via NEFT/RTGS within 30 days of invoice
                          receipt unless otherwise agreed.
                        </li>
                        <li>• For queries, contact billing@ahedge.in</li>
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">
                        Authorised by
                        <span className="text-xs font-medium text-foreground-strong mb-2 ml-1">
                          Ahedge Finance
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 italic">
                        Digitally approved — no signature required
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="disputes" className="mt-0">
                <InfoCard title="" className="mb-6 px-0">
                  {isLoadingDisputes ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                      <span className="ml-2 text-sm text-slate-500">
                        Loading disputes...
                      </span>
                    </div>
                  ) : disputeFilter.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      Please select at least one filter
                    </div>
                  ) : [
                      ...(disputeFilter.includes('pending')
                        ? pendingDisputes
                        : []),
                      ...(disputeFilter.includes('closed')
                        ? verifiedDisputes
                        : []),
                    ].length === 0 ? (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      No disputes found
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border overflow-hidden">
                      {/* Table Header */}
                      <div className="flex items-center bg-[#F8FAFC] border-b border-border">
                        <div className="flex-1 overflow-x-auto">
                          <table className="w-full text-xs table-fixed">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="p-3 py-2 text-center font-normal w-[40px]"></th>
                                <th className="p-3 py-2 text-left font-normal">
                                  CANDIDATE
                                </th>
                                <th className="p-3 py-2 text-right font-normal w-[15%]">
                                  CURRENT CTC
                                </th>
                                <th className="p-3 py-2 text-right font-normal w-[15%]">
                                  MODIFIED CTC
                                </th>
                                <th className="p-3 py-2 text-center font-normal w-[12%]">
                                  STATUS
                                </th>
                              </tr>
                            </thead>
                          </table>
                        </div>
                        <div className="px-3 py-2 w-[40px]"></div>
                      </div>

                      {/* Accordion Table Body */}
                      <Accordion type="single" collapsible className="w-full">
                        {[
                          ...(disputeFilter.includes('pending')
                            ? pendingDisputes
                            : []),
                          ...(disputeFilter.includes('closed')
                            ? verifiedDisputes
                            : []),
                        ].map((dispute) => {
                          const invoiceItem = getItemForDispute(
                            dispute.invoice_item_id
                          );
                          const update =
                            disputeUpdates[dispute.dispute_item_id];
                          // Check if response is complete based on dispute type
                          let hasResponse = false;
                          if (update?.reason) {
                            if (dispute.dispute_type === 'CTC_UPDATE') {
                              hasResponse =
                                update.is_agreed !== null &&
                                (update.is_agreed ||
                                  (update.agreed_amount &&
                                    update.agreed_amount !== ''));
                            } else if (
                              dispute.dispute_type === 'CANDIDATE_REMOVAL'
                            ) {
                              hasResponse = update.is_rejected !== null;
                            } else {
                              hasResponse = true;
                            }
                          }
                          const isClosed = dispute.is_verified;

                          return (
                            <AccordionItem
                              key={dispute.dispute_item_id}
                              value={`dispute-${dispute.dispute_item_id}`}
                              className="border-b border-slate-100 last:border-b-0"
                            >
                              <div className="flex items-center">
                                <div
                                  className="flex-1 cursor-pointer hover:bg-muted transition-colors"
                                  onClick={() =>
                                    handleOpenDisputeChat(
                                      dispute.dispute_item_id
                                    )
                                  }
                                >
                                  <table className="w-full text-xs table-fixed">
                                    <tbody>
                                      <tr>
                                        <td className="p-3 w-[40px] text-center">
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span
                                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                                                  dispute.dispute_type ===
                                                  'CANDIDATE_REMOVAL'
                                                    ? 'bg-red-100 text-red-600'
                                                    : 'bg-brand-bg-subtle text-primary'
                                                }`}
                                              >
                                                {dispute.dispute_type ===
                                                'CANDIDATE_REMOVAL' ? (
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                ) : (
                                                  <SquarePen className="w-3.5 h-3.5" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">
                                                {DISPUTE_TYPE_LABELS[
                                                  dispute.dispute_type
                                                ] || dispute.dispute_type}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </td>
                                        <td className="p-3 font-medium text-slate-800 text-left">
                                          {invoiceItem?.candidateName || 'N/A'}
                                        </td>
                                        <td className="p-3 w-[15%] text-right text-slate-600">
                                          {formatCurrency(
                                            invoiceItem?.ctcAmount
                                          )}
                                        </td>
                                        <td className="p-3 w-[15%] text-right text-slate-600">
                                          {dispute?.dispute_type ===
                                          'CTC_UPDATE'
                                            ? formatCurrency(
                                                dispute?.disputed_ctc_amount
                                              )
                                            : '-'}
                                        </td>
                                        <td className="p-3 w-[12%] text-center">
                                          {isClosed ? (
                                            <span className="inline-flex items-center justify-center gap-1 text-xs text-emerald-600">
                                              <CheckCircle className="w-3.5 h-3.5" />
                                              Closed
                                            </span>
                                          ) : hasResponse ? (
                                            <span className="inline-flex items-center justify-center gap-1 text-xs text-emerald-600">
                                              <CheckCircle className="w-3.5 h-3.5" />
                                              Responded
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center justify-center gap-1 text-xs text-amber-600">
                                              <Clock className="w-3.5 h-3.5" />
                                              Pending
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <AccordionTrigger className="hover:no-underline px-3 py-4 flex-none hover:bg-muted" />
                              </div>
                              <AccordionContent className="px-3 pb-3 pt-0">
                                <div className="bg-muted rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <Briefcase className="w-4 h-4 text-slate-400 mt-0.5" />
                                    <div className="flex-1">
                                      <h4 className="text-xs font-medium text-slate-700 mb-1">
                                        Position Details
                                      </h4>
                                      <p className="text-xs text-slate-600">
                                        {invoiceItem?.requirementTitle || 'N/A'}
                                      </p>
                                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                        <span>
                                          Requirement:{' '}
                                          <span className="text-slate-700">
                                            {invoiceItem?.requirementRegId ||
                                              'N/A'}
                                          </span>
                                        </span>
                                        <span>
                                          PO:{' '}
                                          <span className="text-slate-700">
                                            {invoiceItem?.poNumber || 'N/A'}
                                          </span>
                                        </span>
                                      </div>
                                      {dispute.comments &&
                                        dispute.comments.length > 0 && (
                                          <div className="mt-2 pt-2 border-t border-border">
                                            <span className="text-xs text-slate-500">
                                              {dispute.comments.length}{' '}
                                              comment(s) in conversation
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  )}

                  {/* Sticky Submit Button */}
                  {pendingDisputes.length > 0 && isDisputeFormValid() && (
                    <div className="sticky bottom-0 bg-card p-4 -mx-4 mt-4">
                      <div className="flex items-center justify-end">
                        <Button
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={() => setShowSubmitDisputeAlert(true)}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Submit Response
                        </Button>
                      </div>
                    </div>
                  )}
                </InfoCard>
              </TabsContent>
            </Tabs>
          ) : (
            /* Non-MODIFIED status - show details without tabs */
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-border pb-6">
                <InfoCard title="From" className="pl-0">
                  <h4 className="text-sm font-semibold text-base">
                    {proformaInvoiceData.ahedge.name}
                  </h4>
                  <p className="text-xs text-gray-600 mt-1">
                    {proformaInvoiceData.ahedge.address}
                  </p>
                  <p className="text-xs text-gray-600">
                    {proformaInvoiceData.ahedge.cityStatePin}
                  </p>
                  <p className="text-xs mt-2 flex gap-2 items-center">
                    <span className="text-muted-foreground font-normal text-[11px]">
                      GSTIN:
                    </span>{' '}
                    <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                      {proformaInvoiceData.ahedge.gstin}
                    </span>
                  </p>
                </InfoCard>

                <InfoCard title="To">
                  <h4 className="text-sm font-semibold text-base">
                    {proformaInvoiceData.client.name}
                  </h4>
                  {proformaInvoiceData.client.address && (
                    <>
                      {proformaInvoiceData.client.address.line1 && (
                        <p className="text-xs text-gray-600 mt-1">
                          {proformaInvoiceData.client.address.line1}
                        </p>
                      )}
                      {(proformaInvoiceData.client.address.street ||
                        proformaInvoiceData.client.address.zipCode) && (
                        <p className="text-xs text-gray-600">
                          {[
                            proformaInvoiceData.client.address.street,
                            proformaInvoiceData.client.address.zipCode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}
                    </>
                  )}
                  {proformaInvoiceData.client.gstin &&
                    proformaInvoiceData.client.gstin !== 'N/A' && (
                      <p className="text-xs mt-2 flex gap-2 text-foreground-strong font-semibold items-center">
                        <span className="text-grey-500 font-normal text-xs">
                          GSTIN:
                        </span>{' '}
                        <span className="font-mono text-[11px] font-[550] text-muted-foreground">
                          {proformaInvoiceData.client.gstin}
                        </span>
                      </p>
                    )}
                </InfoCard>
                <InfoCard rounded className="!bg-card grid grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1 justify-between">
                    <span className="text-xs text-gray-700">Invoice Date</span>
                    <span className="text-xs font-bold">
                      {proformaInvoiceData.invoiceDate}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 justify-between">
                    <span className="text-xs text-gray-700">Due Date</span>
                    <span className="text-xs font-bold">
                      {proformaInvoiceData.dueDate}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 justify-between">
                    <span className="text-xs text-gray-700">Status</span>
                    <span className="text-xs font-bold w-fit">
                      <StatusTag status={proformaInvoiceData.meta.status} />
                    </span>
                  </div>
                </InfoCard>
              </div>

              <InfoCard title="Summary" className="mb-6 px-0">
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-border">
                        <tr className="bg-card text-muted-foreground">
                          <th className="p-3 py-2 text-left font-normal">
                            DESCRIPTION
                          </th>
                          <th className="p-3 py-2 text-right font-normal">
                            NOS
                          </th>
                          <th className="p-3 py-2 text-right font-normal">
                            TOTAL
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          key={`item-summary`}
                          className="border-b border-slate-100"
                        >
                          <td className="p-3 font-medium">
                            {proformaInvoiceData.invoiceDetails.description}
                          </td>
                          <td className="p-3 text-right">
                            {proformaInvoiceData.items.length}
                          </td>
                          <td className="p-3 text-right">
                            {currencySymbol}
                            {proformaInvoiceData.invoiceDetails.subTotal?.toLocaleString(
                              'en-IN',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex justify-end p-4 pt-3">
                  <div className="w-full max-w-xs text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">
                        GST ({proformaInvoiceData.invoiceDetails.taxPercentage}
                        %)
                      </span>
                      <span className="text-slate-700 font-medium">
                        {currencySymbol}
                        {proformaInvoiceData.invoiceDetails.taxAmount?.toLocaleString(
                          'en-IN',
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </span>
                    </div>
                    <div className="border-t border-border my-2" />
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground-strong">
                        Grand Total
                      </span>
                      <span
                        className="text-[15px] font-bold text-primary"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {currencySymbol}
                        {proformaInvoiceData.invoiceDetails.totalAmount?.toLocaleString(
                          'en-IN',
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </InfoCard>
              <div className="mt-8 space-y-6">
                <div className="bg-muted border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-xs text-slate-500">
                    This is a system-generated proforma invoice. No signature
                    required.
                  </p>
                </div>

                <div className="flex flex-col gap-4 justify-between items-start pt-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-xs mb-2">Terms</h4>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>
                        • Payment via NEFT/RTGS within 30 days of invoice receipt
                        unless otherwise agreed.
                      </li>
                      <li>• For queries, contact billing@ahedge.in</li>
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">
                      Authorised by
                      <span className="text-xs font-medium text-foreground-strong mb-2 ml-1">
                        Ahedge Finance
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 italic">
                      Digitally approved — no signature required
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {activeTab === 'details' && (
          <div className="shadow-[0_1px_8px_rgba(0,0,0,.08)] rounded-xl border border-border p-6 bg-card">
            <InfoCard title="Invoice Items" className="mb-6 px-0">
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-border">
                      <tr className="bg-card text-muted-foreground">
                        <th className="p-3 py-2 text-left font-normal">
                          CANDIDATE
                        </th>
                        <th className="p-3 py-2 text-left font-normal">
                          REQUIREMENT
                        </th>
                        <th className="p-3 py-2 text-left font-normal">
                          PO NUMBER
                        </th>
                        <th className="p-3 py-2 text-left font-normal">
                          MILESTONE
                        </th>
                        <th className="p-3 py-2 text-right font-normal">CTC</th>
                        <th className="p-3 py-2 text-right font-normal">
                          AMOUNT
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {proformaInvoiceData.items.map((item, index) => (
                        <tr
                          key={`item-${item.id}`}
                          className={`${
                            index < proformaInvoiceData.items.length - 1
                              ? 'border-b border-slate-100'
                              : ''
                          }`}
                        >
                          <td className="p-3 font-medium">
                            {item.candidateName}
                          </td>
                          <td className="p-3">{item.requirementRegId}</td>
                          <td className="p-3">{item.poNumber}</td>
                          <td className="p-3">
                            {item.milestoneText}
                            <div className="text-xs mt-1">
                              {getMilestoneLabel(item)}
                            </div>
                          </td>
                          <td className="p-3 text-right">
                            {currencySymbol}
                            {item.ctcAmount?.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="p-3 text-right font-medium">
                            {currencySymbol}
                            {item.amount?.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </InfoCard>
          </div>
        )}
      </div>

      <Alert
        isOpen={showVerifyAlert}
        onClose={() => setShowVerifyAlert(false)}
        title="Verify proforma invoice"
        description={`Are you sure you want to Verify proforma invoice ${proformaInvoiceData.invoiceNumber}?`}
        cancelText="Cancel"
        continueText={isVerifying ? 'Processing...' : 'Verify'}
        onCancel={() => setShowVerifyAlert(false)}
        onContinue={handleVerifyProformaInvoice}
      />

      <Alert
        isOpen={showSubmitDisputeAlert}
        onClose={() => setShowSubmitDisputeAlert(false)}
        title="Submit Dispute Response"
        description="Once you submit, the agreed changes will be sent to the customer and no further edits will be possible. Proceed?"
        cancelText="Cancel"
        continueText={isSubmittingDisputes ? 'Submitting...' : 'Submit'}
        onCancel={() => setShowSubmitDisputeAlert(false)}
        onContinue={handleSubmitDisputes}
      />

      {/* Dispute Chat Side Panel */}
      <Sheet open={showDisputeChat} onOpenChange={setShowDisputeChat}>
        <SheetContent
          side="right"
          className="w-[450px] sm:max-w-[450px] p-0 flex flex-col gap-0"
          closeClassName="text-white hover:text-white/80 focus:outline-none focus:ring-0"
        >
          {(() => {
            // Look in both pending and verified disputes
            const selectedDispute =
              pendingDisputes.find(
                (d) => d.dispute_item_id === selectedDisputeId
              ) ||
              verifiedDisputes.find(
                (d) => d.dispute_item_id === selectedDisputeId
              );
            const selectedInvoiceItem = selectedDispute
              ? getItemForDispute(selectedDispute.invoice_item_id)
              : null;
            const isClosed = selectedDispute?.is_verified;

            if (!selectedDispute) return null;

            return (
              <>
                <SheetHeader className="px-4 py-3 border-b border-border flex-shrink-0 bg-[#1B6AAF]">
                  <SheetTitle className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      <span>
                        {isClosed ? 'Dispute Details' : 'Dispute Conversation'}
                      </span>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* Candidate Info & Dispute Type */}
                  <div className="mx-4 mt-4 px-4 py-3 border border-border bg-muted rounded-md flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-md font-semibold text-slate-800">
                        {selectedInvoiceItem?.candidateName || 'N/A'}
                      </h4>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${
                          selectedDispute.dispute_type === 'CANDIDATE_REMOVAL'
                            ? 'bg-red-50 text-red-600 border-red-200'
                            : 'bg-brand-bg-subtle text-primary border-input'
                        }`}
                      >
                        {selectedDispute.dispute_type ===
                        'CANDIDATE_REMOVAL' ? (
                          <Trash2 className="w-3 h-3" />
                        ) : (
                          <SquarePen className="w-3 h-3" />
                        )}
                        {DISPUTE_TYPE_LABELS[selectedDispute.dispute_type] ||
                          selectedDispute.dispute_type}
                      </span>
                    </div>

                    {/* CTC_UPDATE: Amount Info with Agree/Disagree */}
                    {selectedDispute.dispute_type === 'CTC_UPDATE' && (
                      <>
                        <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                          <div className="bg-card rounded-lg p-2 border border-border">
                            <span className="text-muted-foreground block text-[10px]">
                              Original
                            </span>
                            <span className="font-semibold text-foreground-strong">
                              {formatCurrency(
                                selectedDispute.original_ctc_amount
                              )}
                            </span>
                          </div>
                          <div className="bg-white dark:bg-amber-950/20 rounded-lg p-2 border border-amber-200 dark:border-amber-800/50">
                            <span className="text-slate-500 block text-[10px]">
                              Disputed
                            </span>
                            <span className="font-semibold text-amber-600">
                              {formatCurrency(
                                selectedDispute.disputed_ctc_amount
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Agree/Disagree Buttons for CTC_UPDATE */}
                        {!isClosed && (
                          <div className="mt-3">
                            <span className="text-xs text-slate-600 block mb-2">
                              Do you agree with the disputed CTC amount?
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_agreed === true
                                    ? 'default'
                                    : 'outline'
                                }
                                className={`flex-1 text-xs ${
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_agreed === true
                                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleFeeUpdateDecision(
                                    selectedDispute.dispute_item_id,
                                    true,
                                    selectedDispute.disputed_amount
                                  )
                                }
                              >
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                Agree
                              </Button>
                              <Button
                                size="sm"
                                variant={
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_agreed === false
                                    ? 'default'
                                    : 'outline'
                                }
                                className={`flex-1 text-xs ${
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_agreed === false
                                    ? 'bg-red-500 hover:bg-red-600 text-white'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleFeeUpdateDecision(
                                    selectedDispute.dispute_item_id,
                                    false,
                                    selectedDispute.disputed_amount
                                  )
                                }
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Disagree
                              </Button>
                            </div>

                            {/* Show input for new agreed amount if disagree */}
                            {disputeUpdates[selectedDispute.dispute_item_id]
                              ?.is_agreed === false && (
                              <div className="mt-3 bg-card rounded-lg p-3 border border-border">
                                <span className="text-xs text-slate-600 block mb-2">
                                  Enter your agreed amount *
                                </span>
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
                                    {currencySymbol}
                                  </span>
                                  <Input
                                    type="number"
                                    placeholder="Enter amount"
                                    className="h-8 text-xs pl-6 w-full"
                                    value={
                                      disputeUpdates[
                                        selectedDispute.dispute_item_id
                                      ]?.agreed_amount || ''
                                    }
                                    onChange={(e) =>
                                      handleAgreedAmountChange(
                                        selectedDispute.dispute_item_id,
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Show agreed amount for closed disputes */}
                        {isClosed && selectedDispute.agreed_amount && (
                          <div className="mt-3 bg-emerald-50 rounded-lg p-2 border border-emerald-200">
                            <span className="text-slate-500 block text-[10px]">
                              Agreed Amount
                            </span>
                            <span className="font-semibold text-emerald-600 text-xs">
                              {formatCurrency(selectedDispute.agreed_amount)}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* CANDIDATE_REMOVAL: Agree/Disagree Buttons */}
                    {selectedDispute.dispute_type === 'CANDIDATE_REMOVAL' && (
                      <>
                        <div className="text-xs text-slate-500 mt-2">
                          Original Amount:{' '}
                          <span className="font-medium text-slate-700">
                            {formatCurrency(selectedDispute.amount)}
                          </span>
                        </div>

                        {/* Agree/Disagree Buttons for CANDIDATE_REMOVAL */}
                        {!isClosed && (
                          <div className="mt-3">
                            <span className="text-xs text-slate-600 block mb-2">
                              Do you agree to remove this candidate?
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_rejected === false
                                    ? 'default'
                                    : 'outline'
                                }
                                className={`flex-1 text-xs ${
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_rejected === false
                                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleCandidateRemovalDecision(
                                    selectedDispute.dispute_item_id,
                                    true // agree = is_rejected false
                                  )
                                }
                              >
                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                Agree
                              </Button>
                              <Button
                                size="sm"
                                variant={
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_rejected === true
                                    ? 'default'
                                    : 'outline'
                                }
                                className={`flex-1 text-xs ${
                                  disputeUpdates[
                                    selectedDispute.dispute_item_id
                                  ]?.is_rejected === true
                                    ? 'bg-red-500 hover:bg-red-600 text-white'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleCandidateRemovalDecision(
                                    selectedDispute.dispute_item_id,
                                    false // disagree = is_rejected true
                                  )
                                }
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Disagree
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Show decision status for closed disputes */}
                        {isClosed && (
                          <div
                            className={`mt-3 rounded-lg p-2 border text-xs w-fit ${
                              selectedDispute.is_rejected === false
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                            }`}
                          >
                            {selectedDispute.is_rejected === false
                              ? 'Candidate removal was accepted'
                              : 'Candidate removal was rejected'}
                          </div>
                        )}
                      </>
                    )}

                    {/* Other dispute types - show original amount only */}
                    {selectedDispute.dispute_type !== 'CTC_UPDATE' &&
                      selectedDispute.dispute_type !== 'CANDIDATE_REMOVAL' && (
                        <div className="text-xs text-slate-500 mt-1">
                          Original Amount:{' '}
                          <span className="font-medium text-slate-700">
                            {formatCurrency(selectedDispute.amount)}
                          </span>
                        </div>
                      )}
                  </div>
                  {/* Chat Messages */}
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-3 px-4 py-4">
                    {selectedDispute.comments &&
                    selectedDispute.comments.length > 0 ? (
                      selectedDispute.comments.map((comment, idx) => {
                        const isCustomer = comment.user_type === 'CUSTOMER';

                        // Determine colors based on user type
                        const bubbleClass = isCustomer
                          ? 'bg-brand-bg-subtle'
                          : 'bg-emerald-50 ';

                        return (
                          <div
                            key={idx}
                            className={`rounded-lg p-3 ${bubbleClass}`}
                          >
                            {/* System message as header */}
                            {comment.system && (
                              <div className="flex items-center gap-2 mb-2 pb-2">
                                <span className="text-xs font-semibold bg-card text-muted-foreground border border-gray-200 dark:border-gray-700/60 px-2 py-0.5 rounded">
                                  {comment.system}
                                </span>
                                <p className="text-[10px] text-slate-400">
                                  {formatDate(comment.timestamp)}
                                </p>
                              </div>
                            )}

                            <div className="flex items-start justify-between gap-3">
                              <div className="flex flex-col">
                                {/* Comment reason */}
                                <p className="text-sm text-slate-700 flex-1 ml-2">
                                  {comment.reason}
                                </p>
                              </div>
                              {/* User info and timestamp */}
                              <div className="flex-shrink-0 text-right">
                                <p className="text-xs font-normal text-slate-400">
                                  {comment.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-slate-400 text-xs">
                        {isClosed
                          ? 'No comments in this dispute.'
                          : 'No comments yet. Add your response below.'}
                      </div>
                    )}
                  </div>

                  {/* Add Response Input - Only show for pending disputes */}
                  {!isClosed && (
                    <div className="p-4 bg-card flex-shrink-0 border-t border-border">
                      <label className="text-xs font-medium text-slate-600 block mb-2">
                        Your Response *
                      </label>
                      <Textarea
                        placeholder="Enter your comment or reason..."
                        className="text-xs min-h-[80px] resize-none mb-3"
                        value={
                          disputeUpdates[selectedDispute.dispute_item_id]
                            ?.reason || ''
                        }
                        onChange={(e) =>
                          handleReasonChange(
                            selectedDispute.dispute_item_id,
                            e.target.value
                          )
                        }
                      />
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowDisputeChat(false)}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ProformaInvoiceInfo;
